import { html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import { pageMetadata } from '@flakiness/server/common/pageMetadata.js';
import { ReportMetrics } from '@flakiness/server/common/reportMetrics.js';
import { CHECK } from '@flakiness/shared/common/utils.js';
import { ContextType } from '@lit/context';
import { Task } from '@lit/task';
import { api } from './api.js';
import { pageStyles } from './components/cssstyles.js';
import { OrgAlerts } from './components/org-alerts.js';
import { OrganizationContext } from './components/organization-context.js';
import { consume, contexts } from './contexts.js';
import { createProjectsDataProvider } from './data-table-providers/projectsProvider.js';
import { platformUrl } from './platformUrl.js';
import { RouteConfig } from './router.js';
import { tasks } from './tasks.js';


@customElement('page-organization-usage')
export class PageOrganizationUsage extends LitElement {

  static url(options: { orgSlug: string, tab?: 'billing'|'general'|'members' }): string {
    return platformUrl(['', options.orgSlug, 'usage'].join('/'));
  }

  static routes(): RouteConfig[] {
    return [{
      path: [
        '/:org/usage',
      ],
      render: (groups) => OrganizationContext.render({
        orgSlug: groups?.org,
      }, html`
        <page-organization-usage
          .orgSlug=${groups?.org}
        ></page-organization-usage>
      `),
    }];
  }

  @consume({ context: contexts.org, subscribe: true }) private _org?: ContextType<typeof contexts.org>;

  @property({ attribute: false }) orgSlug?: string;

  private _projectsTask = new Task(this, {
    args: () => [this._org] as const,
    task: async ([org], { signal }) => {
      CHECK(org?.access);
      const projects = await api.organization.listProjects.GET({ orgSlug: org.orgSlug }, { signal });
      const totals: ReportMetrics.Counters[] = [];
      for (const project of projects) {
        const snapshot = project.snapshot;
        if (!snapshot)
          continue;
        totals.push({
          reportBytes: snapshot.reportBytes,
          attachmentBytes: snapshot.attachmentBytes,
          runsCount: snapshot.runsCount,
          testsCount: snapshot.testRunsCount,
        });
      }
      return {
        totals,
        provider: createProjectsDataProvider(projects),
      };
    },
  });


  private _pageMetadataTask = tasks.pageMetadata(this, {
    args: () => [this.orgSlug] as const,
    metadata: ([orgSlug]) => orgSlug ? pageMetadata({ orgSlug, section: 'Usage', accessGated: true }) : undefined,
  });

  render() {
    if (this._org && !this._org.access)
      return html`<page-http-error code=403></page-http-error>`;

    return html`
      <org-header .orgSlug=${this.orgSlug} submenu=usage></org-header>
      <app-body medium user-role=${this._org?.access}>
        ${OrgAlerts.maybeRender(this._org)}
        <usage-totals heading="Organization Usage" .metrics=${this._projectsTask.value?.totals}></usage-totals>
        ${this._projectsTask.render({
          pending: () => html`<sl-spinner></sl-spinner>`,
          error: () => html`<fk-callout variant=danger>Could not load project usage.</fk-callout>`,
          complete: ({ provider }) => html`
            <data-table
              .provider=${provider}
              .pageNumber=${0}
              .pageSize=${20}
              .noDataMessage=${'This organization has no projects.'}
            ></data-table>
          `,
        })}
      </app-body>
      <app-footer></app-footer>
    `
  }

  static styles = [pageStyles];
}
